import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { buildNSCEmail } from '@nsc/smartteams-shared/email-helpers.js';
import { html } from 'lit';
import { config } from '../config.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

function normalizePassword(password?: string): string | undefined {
  const value = password?.trim();
  return value ? value : undefined;
}

/**
 * Sends an onboarding email to a customer login. Mirrors `sendFieldEngineerOnboardingEmail`
 * but addressed to a customer rather than a field engineer. Permission gate: UserManage
 * (since customer logins live alongside other admin-managed user accounts).
 */
export const sendCustomerOnboardingEmail = registerMutation({
  operationName: 'sendCustomerOnboardingEmail',
  type: () => String,
  params: [
    { name: 'userId', type: () => String },
    { name: 'password', type: () => String },
  ],
  resolve: (userId: string, password?: string) => async () => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('No permission to notify customer logins');

    const userCol = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    const user = await userCol.findOne({ _id: userId }, { projection: { _id: 1, email: 1 } });
    if (!user) throw new Error('User not found');
    if (!user.email) throw new Error('User has no email address');

    const givenPassword = normalizePassword(password);

    const message = await Message.compose({
      subject: 'NSC Global: Welcome to SmartTeams',
      html: buildNSCEmail(html`
        <div>
          <p>Welcome to the NSC SmartTeams app!</p>
          <p>
            Your customer account has been created. Use the credentials below to log in and review the
            tickets your organisation has open with NSC.
          </p>
          <p><strong>Username:</strong> ${user.email}</p>
          <p><strong>Temporary Password:</strong> ${givenPassword ?? 'Provided by your administrator'}</p>
          <p>You'll be prompted to change your password at first login.</p>
          <p>
            <a
              href="${config.get('APP_URL')}"
              style="display: inline-block; background-color: #f37021; color: white; margin-top: 12px; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;"
            >
              Open SmartTeams App
            </a>
          </p>
          <p>Or click on this link: <a href="${config.get('APP_URL')}" target="_blank">${config.get('APP_URL')}</a></p>
        </div>
      `),
    });

    await AdornisMailer.getInstance().sendMail(message, new Recipient(user.email));

    return `sent onboarding email to ${user.email}`;
  },
});
